import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Alert, Button, Col, Form, FormFeedback, Input, Label, Row, Spinner } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAuth } from "auth/useAuth";
import { translateAuthError } from "auth/cognitoClient";
import { setSignupHandoff } from "auth/signupHandoff";
import { track, EVENTS } from "telemetry/track";
import AuthLayout from "./AuthLayout";

const PASSWORD_RULES = "Mínimo de 8 caracteres, com letra maiúscula, minúscula, número e símbolo.";

const SignUp = () => {
  document.title = "Criar conta | ACIG Guapiaçu Cidadão";

  const auth = useAuth();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => { track(EVENTS.SIGNUP_STARTED); }, []);

  const form = useFormik({
    initialValues: { firstName: "", lastName: "", email: "", password: "", confirm: "", consent: false },
    validationSchema: Yup.object({
      firstName: Yup.string().trim().min(2, "Informe seu nome.").required("Informe seu nome."),
      lastName: Yup.string().trim().min(2, "Informe seu sobrenome.").required("Informe seu sobrenome."),
      email: Yup.string().trim().email("Informe um e-mail válido.").required("Informe seu e-mail."),
      password: Yup.string()
        .min(8, PASSWORD_RULES)
        .matches(/[a-z]/, PASSWORD_RULES)
        .matches(/[A-Z]/, PASSWORD_RULES)
        .matches(/\d/, PASSWORD_RULES)
        .matches(/[^A-Za-z0-9]/, PASSWORD_RULES)
        .required("Crie uma senha."),
      confirm: Yup.string().oneOf([Yup.ref("password")], "As senhas não coincidem.").required("Confirme a senha."),
      consent: Yup.boolean().oneOf([true], "Você precisa aceitar para continuar."),
    }),
    onSubmit: async ({ firstName, lastName, email, password }) => {
      setSubmitError(null);
      try {
        const result = await auth.signUp({ email, password, firstName, lastName });
        track(EVENTS.SIGNUP_SUBMITTED);
        if (result.status === "CONFIRMATION_REQUIRED") {
          setSignupHandoff(result.email, password);
          navigate("/confirmar-conta", { state: { email: result.email, justSignedUp: true } });
        } else {
          navigate("/login", { state: { email: result.email, flash: "Conta criada! Entre para continuar." } });
        }
      } catch (err) {
        console.error("Sign-up failed:", err);
        track(EVENTS.SIGNUP_FAILED, { code: err?.code || "unknown" });
        if (err?.code === "UsernameExistsException") {
          setSubmitError(
            <>
              Já existe uma conta com este e-mail.{" "}
              <Link to="/login" state={{ email: form.values.email.trim().toLowerCase() }}>Entrar</Link>
              {" ou "}
              <Link to="/esqueci-senha" state={{ email: form.values.email.trim().toLowerCase() }}>recuperar a senha</Link>.
            </>
          );
        } else {
          setSubmitError(translateAuthError(err));
        }
      }
    },
  });

  if (!auth.isLoading && auth.isAuthenticated) return <Navigate to="/" replace />;

  const field = (name) => ({
    name,
    value: form.values[name],
    onChange: form.handleChange,
    onBlur: form.handleBlur,
    invalid: !!(form.touched[name] && form.errors[name]),
  });

  return (
    <AuthLayout
      title="Crie sua conta"
      subtitle="Leva menos de um minuto."
      footer={
        <p className="mb-0">
          Já tem uma conta?{" "}
          <Link to="/login" className="fw-medium text-primary">Entrar</Link>
        </p>
      }
    >
      <Form onSubmit={form.handleSubmit} noValidate>
        {submitError && <Alert color="danger">{submitError}</Alert>}

        <Row>
          <Col sm={6} className="mb-3">
            <Label htmlFor="su-firstName">Nome</Label>
            <Input id="su-firstName" type="text" autoComplete="given-name" placeholder="Seu nome" autoFocus {...field("firstName")} />
            <FormFeedback>{form.errors.firstName}</FormFeedback>
          </Col>
          <Col sm={6} className="mb-3">
            <Label htmlFor="su-lastName">Sobrenome</Label>
            <Input id="su-lastName" type="text" autoComplete="family-name" placeholder="Seu sobrenome" {...field("lastName")} />
            <FormFeedback>{form.errors.lastName}</FormFeedback>
          </Col>
        </Row>

        <div className="mb-3">
          <Label htmlFor="su-email">E-mail</Label>
          <Input id="su-email" type="email" inputMode="email" autoComplete="email" placeholder="voce@exemplo.com.br" {...field("email")} />
          <FormFeedback>{form.errors.email}</FormFeedback>
          <div className="form-text">Vamos enviar um código de confirmação para este e-mail.</div>
        </div>

        <div className="mb-3">
          <Label htmlFor="su-password">Senha</Label>
          <div className="input-group auth-pass-inputgroup">
            <Input id="su-password" type={showPassword ? "text" : "password"} autoComplete="new-password" placeholder="Crie uma senha" {...field("password")} />
            <button type="button" className="btn btn-light" onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}>
              <i className={`mdi ${showPassword ? "mdi-eye-off-outline" : "mdi-eye-outline"}`} />
            </button>
            <FormFeedback>{form.errors.password}</FormFeedback>
          </div>
          {!(form.touched.password && form.errors.password) && <div className="form-text">{PASSWORD_RULES}</div>}
        </div>

        <div className="mb-3">
          <Label htmlFor="su-confirm">Confirmar senha</Label>
          <Input id="su-confirm" type={showPassword ? "text" : "password"} autoComplete="new-password" placeholder="Repita a senha" {...field("confirm")} />
          <FormFeedback>{form.errors.confirm}</FormFeedback>
        </div>

        <div className="mb-3 form-check">
          <Input id="su-consent" type="checkbox" className="form-check-input" name="consent"
            checked={form.values.consent} onChange={form.handleChange} onBlur={form.handleBlur}
            invalid={!!(form.touched.consent && form.errors.consent)} />
          <Label htmlFor="su-consent" className="form-check-label">
            Concordo que meus dados sejam usados pela ACIG para fins de recrutamento e contato.
          </Label>
          <FormFeedback>{form.errors.consent}</FormFeedback>
        </div>

        <div className="d-grid">
          <Button color="primary" type="submit" disabled={form.isSubmitting}>
            {form.isSubmitting && <Spinner size="sm" className="me-2" />}
            Criar conta
          </Button>
        </div>
      </Form>
    </AuthLayout>
  );
};

export default SignUp;
